import { useEffect, useRef, useState } from 'react';
import '../../css/PlaceAutocomplete.css';

export default function PlaceAutocompleteInput({
    id,
    value,
    name,
    placeholder,
    description,
    regionCodes,
    variant = '',
    disabled = false,
    fallback,
    onInput,
    onPlaceSelect,
    onError,
    onReady,
}) {
    const inputRef = useRef(null);
    const containerRef = useRef(null);
    const gestureRef = useRef(null);
    const selectionRef = useRef(0);
    const selectingRef = useRef(false);
    const callbacksRef = useRef({ onInput, onPlaceSelect, onError, onReady });
    const tokenRef = useRef(null);
    const requestIdRef = useRef(0);
    const [placesLibrary, setPlacesLibrary] = useState(null);
    const [focused, setFocused] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [activeIndex, setActiveIndex] = useState(-1);
    const listId = `${id}-suggestions`;

    callbacksRef.current = { onInput, onPlaceSelect, onError, onReady };

    useEffect(() => {
        const closeOutside = event => {
            if (!containerRef.current?.contains(event.target)) {
                gestureRef.current = null;
                setFocused(false);
            }
        };
        document.addEventListener('pointerdown', closeOutside, true);
        return () => {
            document.removeEventListener('pointerdown', closeOutside, true);
        };
    }, []);

    useEffect(() => {
        let disposed = false;
        window.google.maps.importLibrary('places')
            .then(library => { if (!disposed) setPlacesLibrary(library); })
            .catch(() => { if (!disposed) callbacksRef.current.onError?.(); });
        return () => { disposed = true; };
    }, []);

    useEffect(() => {
        if (!placesLibrary) return undefined;
        callbacksRef.current.onReady?.(inputRef.current);
        return () => callbacksRef.current.onReady?.(null);
    }, [placesLibrary]);

    useEffect(() => {
        let disposed = false;
        const requestId = ++requestIdRef.current;
        const query = value.trim();
        setSuggestions([]);
        setActiveIndex(-1);
        if (!placesLibrary || !focused || disabled || !query) {
            if (!query) tokenRef.current = null;
            return undefined;
        }

        const timeout = window.setTimeout(async () => {
            try {
                if (!tokenRef.current) tokenRef.current = new placesLibrary.AutocompleteSessionToken();
                const { suggestions: results } = await placesLibrary.AutocompleteSuggestion.fetchAutocompleteSuggestions({
                    input: query,
                    language: 'it',
                    region: 'it',
                    ...(regionCodes ? { includedRegionCodes: regionCodes } : {}),
                    sessionToken: tokenRef.current,
                });
                if (disposed || requestId !== requestIdRef.current) return;
                const predictions = results.map(result => result.placePrediction).filter(Boolean);
                setSuggestions(predictions);
            } catch {
                if (disposed || requestId !== requestIdRef.current) return;
                setSuggestions([]);
                callbacksRef.current.onError?.();
            }
        }, 250);

        return () => {
            disposed = true;
            window.clearTimeout(timeout);
        };
    }, [value, focused, disabled, placesLibrary, regionCodes]);

    async function selectPlace(prediction) {
        if (disabled || selectingRef.current) return;
        selectingRef.current = true;
        const selectionId = ++selectionRef.current;
        const selectedValue = inputRef.current?.value;
        ++requestIdRef.current;
        setFocused(false);
        setSuggestions([]);
        setActiveIndex(-1);
        inputRef.current?.blur();
        tokenRef.current = null;
        try {
            const place = prediction.toPlace();
            await place.fetchFields({ fields: ['formattedAddress', 'location'] });
            if (selectionId !== selectionRef.current || !inputRef.current || inputRef.current.value !== selectedValue) return;
            const lat = place.location?.lat();
            const lng = place.location?.lng();
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) throw new Error('Missing place location');
            callbacksRef.current.onPlaceSelect?.({
                address: place.formattedAddress || prediction.text.toString(),
                lat,
                lng,
                placeId: place.id,
            });
        } catch {
            if (selectionId === selectionRef.current && inputRef.current?.value === selectedValue) callbacksRef.current.onError?.();
        } finally {
            if (selectionId === selectionRef.current) selectingRef.current = false;
        }
    }

    function startGesture(event) {
        if (event.pointerType === 'mouse' || event.isPrimary === false) return;
        // Keep the keyboard from moving the list before pointerup. Native vertical
        // scrolling remains available through touch-action and pointercancel.
        event.preventDefault();
        gestureRef.current = { id: event.pointerId, x: event.clientX, y: event.clientY, moved: false };
    }

    function moveGesture(event) {
        const gesture = gestureRef.current;
        if (gesture?.id === event.pointerId && Math.hypot(event.clientX - gesture.x, event.clientY - gesture.y) > 10) {
            gesture.moved = true;
        }
    }

    function finishGesture(event, prediction) {
        const gesture = gestureRef.current;
        if (!gesture || gesture.id !== event.pointerId) return;
        moveGesture(event);
        const bounds = event.currentTarget.getBoundingClientRect();
        const inside = event.clientX >= bounds.left && event.clientX <= bounds.right && event.clientY >= bounds.top && event.clientY <= bounds.bottom;
        if (!gesture.moved && inside) {
            // Complete a tap before iOS blurs the field or synthesizes a mouse click.
            event.preventDefault();
            selectPlace(prediction);
        }
        // Retain the moved flag until the next pointerdown to ignore a click after a drag.
    }

    function handleKeyDown(event) {
        if (event.key === 'Tab') {
            gestureRef.current = null;
            setFocused(false);
        }
        if (!suggestions.length) return;
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveIndex(index => (index + 1) % suggestions.length);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex(index => (index - 1 + suggestions.length) % suggestions.length);
        } else if (event.key === 'Enter' && activeIndex >= 0) {
            event.preventDefault();
            selectPlace(suggestions[activeIndex]);
        } else if (event.key === 'Escape') {
            event.preventDefault();
            setFocused(false);
            setSuggestions([]);
            inputRef.current?.blur();
        }
    }

    if (!placesLibrary) return fallback;

    return (
        <div ref={containerRef} className={`ef-place-autocomplete${variant ? ` ef-place-autocomplete--${variant}` : ''}`}>
            <input
                ref={inputRef}
                id={id}
                name={name}
                type="text"
                value={value}
                placeholder={placeholder}
                aria-label={description}
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={focused && suggestions.length > 0}
                aria-controls={focused && suggestions.length ? listId : undefined}
                aria-activedescendant={focused && activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
                autoComplete="off"
                disabled={disabled}
                onFocus={() => { gestureRef.current = null; setFocused(true); }}
                onBlur={event => {
                    if (!gestureRef.current && !containerRef.current?.contains(event.relatedTarget)) setFocused(false);
                }}
                onChange={event => {
                    ++selectionRef.current;
                    selectingRef.current = false;
                    callbacksRef.current.onInput?.(event.target.value);
                }}
                onKeyDown={handleKeyDown}
            />
            {focused && suggestions.length > 0 && (
                <div className="ef-place-autocomplete__dropdown">
                    <ul id={listId} role="listbox" aria-label="Indirizzi suggeriti">
                        {suggestions.map((prediction, index) => (
                            <li key={`${prediction.placeId}-${index}`} role="presentation">
                                <button
                                    id={`${listId}-${index}`}
                                    type="button"
                                    role="option"
                                    aria-selected={index === activeIndex}
                                    tabIndex={-1}
                                    onPointerDown={event => {
                                        gestureRef.current = null;
                                        startGesture(event);
                                    }}
                                    onPointerMove={moveGesture}
                                    onPointerUp={event => finishGesture(event, prediction)}
                                    onPointerCancel={() => { if (gestureRef.current) gestureRef.current.moved = true; }}
                                    onMouseDown={event => event.preventDefault()}
                                    onClick={event => {
                                        if (event.detail === 0 || !gestureRef.current?.moved) selectPlace(prediction);
                                    }}
                                >{prediction.text.toString()}</button>
                            </li>
                        ))}
                    </ul>
                    <div className="ef-place-autocomplete__attribution" translate="no">Google Maps</div>
                </div>
            )}
        </div>
    );
}
